import {Environment} from "appRoot/app.constants";
import {AdminServiceLog, CouchEndpointStatus, CouchStatus, ReplicationEventType} from "app/app.types";
import NetworkConnectionService from 'external-services/network-connection-service/index';
import UtilsService from "external-services/utils-service/index";
import TelemetryService from "app/services/telemetry/telemetry.service";
import PouchActions, {PouchDocType} from "app/actions/pouch.actions";
import AdminService from "external-services/diary-admin-service/admin.service";
import PouchBulkService from 'external-services/pouch-bulk-service';
import DateUtilsService from 'external-services/date-utils-service';
import PouchSyncService from "external-services/pouch-sync-service/index";
import PouchLoadService from "external-services/pouch-load-service";
import LoginActions from "app/actions/login.actions";
import VenueActions, {VenueActionNames} from "app/actions/venue.actions";

const NS = 'PouchService'; // jshint ignore:line

export default function PouchService(
  $rootScope, $http, $q, $window, $localStorage,
  AuthService
) {
  "ngInject";

  var logTag = '[Pouch DB] ==>';
  var daysInHistory = 2;

  // Public interface
  var service = {
    settings: {},
    setup: false,
    created: false,
    session: {
      docsSent: 0,
      docsReceived: 0,
      localChanges: [],
      remoteChanges: []
    },

    // Functions
    init,
    createDatabase,
    setUniqueVenueID,
    getDBOnlineReducedAsFirstOption,
    getRemoteDatebaseInfo,
    getNewId,
    addOnlineListener,
    removeOnlineListener,
    destroyDatabases,
    getNewOfflineBookingsInstance,
    getLocalDbUpperDateLimit
  };

  // Private attributes
  var remoteDBDocsCount;
  var isActive = true;
  var changesInBatch = 0;
  var maxChangeBatch = 50;
  var onlineChanges;
  var updatedDocIds = {};
  var syncing = false;
  var syncSuccess = false;
  var venueId = 0;
  var syncCheckInterval = null;
  var syncTypes = {};
  var resyncTries = 0;
  var syncLogStack = [];
  var replicateSubscriptions = {
    initialFrom: null,
    liveFrom: null,
    liveTo: null
  };

  var currentVenueIdLoading;

  function init() {
    // Close DB on Lougout or VenueChange
    LoginActions.handleLogout(() => closeDatabase());
    VenueActions.handleVenueAction(VenueActionNames.VENUE_CHANGE, () => closeDatabase());
    $rootScope.$on('change-date', changeDate);
  }


  function getDBOnlineReducedAsFirstOption() {
    return (NetworkConnectionService.isOnline() ? PouchLoadService.getDBOnlineReduced() : PouchLoadService.getDbLocal());
  }

  function setUniqueVenueID(currentVenueId) {
    currentVenueIdLoading = currentVenueId;
  }

  /**
   * Moved getDBOnlineReduced into Shared PouchLoadService
   */

  /********************************************************************************************************************************************************** */
  /******************************************************************* CREATE DATABASE ********************************************************************** */
  /********************************************************************************************************************************************************** */

  function createDatabase(localPouchName, fullInfo, reducedInfo, venueId, _currentVenueIdLoading) {

    const startTime = new Date().getTime();
    remoteDBDocsCount = -2;
    try{
      console.log(logTag, `0. Starting db creating proc localPouchName: ${localPouchName}, fullInfo: ${fullInfo?.url ||''}, reducedInfo: ${reducedInfo?.url || ''}, venueId: ${venueId} >>`);
      syncLogStack = [`0. Starting db creating proc localPouchName: ${localPouchName}, fullInfo: ${fullInfo?.url ||''}, reducedInfo: ${reducedInfo?.url || ''}, venueId: ${venueId} >>`];
    }
    catch (err)
    {
      console.error(`0. Starting db creating -> log ${err}`);
    }

    try
    {
      closeDatabase();  // ZS FIX ? not sure but could it be crashing close iti, this wont hurt.
    }
    catch (err)
    {
      closeDatabase();
    }


    // wait till remotes are closed
    return $q.when(PouchLoadService.checkDatabaseClosed(syncLogStack, replicateSubscriptions)
      .then(() => {
        throwErrorOnMismatch(venueId, 1, _currentVenueIdLoading);
        service.settings.fullInfo = fullInfo;
        service.settings.reducedInfo = reducedInfo;

        // sets the replicateSubscriptions so it can be used for future sync operations
        PouchSyncService.setReplicateSubscriptions(replicateSubscriptions);

        return PouchLoadService.createDbLocal(localPouchName, syncLogStack, reducedInfo);
      })
      .then(db => {
        throwErrorOnMismatch(venueId, 2, _currentVenueIdLoading);
        PouchBulkService.loadDump(syncLogStack, reducedInfo, db, venueId, () => AuthService.getBearerToken())
      })
      .then(() => {
        throwErrorOnMismatch(venueId, 3, _currentVenueIdLoading);
        PouchSyncService.getRemoteDBDocsCount(syncLogStack, reducedInfo)
        }) // Used for load progress feedback
      .then(_remoteDBDocsCount => {
        throwErrorOnMismatch(venueId, 4, _currentVenueIdLoading);
        remoteDBDocsCount = _remoteDBDocsCount;
      })
      .then(addListeners)
      // .then(() => createOnlineOnlyDataBase(0))
      .then(() => {
        throwErrorOnMismatch(305, 5, _currentVenueIdLoading);
        return PouchLoadService.createOnlineOnlyDataBase(service.settings.fullInfo, syncLogStack, () => AuthService.getBearerToken(), 0);
      })
      // Update local DB to remote latest status
      // On complete notify $broadcast('uptodate-with-remote') and resolve
      .then(dbOnline => {
        throwErrorOnMismatch(venueId, 6, _currentVenueIdLoading);
        const isOnlineCreated = !!dbOnline;
        console.log('isOnlineCreated', isOnlineCreated);

        // if remote couch failed, we set the app to offline mode
        NetworkConnectionService.setCouchEndpointStatus(isOnlineCreated ? CouchEndpointStatus.isReachable : CouchEndpointStatus.isUnreachable);

        return initialReplicateFrom(isOnlineCreated, 0);
      })
      // .then(initialReplicateFrom)
      //.then(verifyReplication)
      // Bidirection live sync - resolve immedialty after been set up
      .then(liveSync)
      .then(() => {
        throwErrorOnMismatch(venueId, 7, _currentVenueIdLoading);
        try {
          console.log(logTag, `11. DB Create completed, starting async post-operations localPouchName: ${localPouchName}, fullInfo: ${fullInfo?.url || ''}, reducedInfo: ${reducedInfo?.url || ''}, venueId: ${venueId} >>`);
          syncLogStack.push(`11. DB Create completed, starting async post-operations localPouchName: ${localPouchName}, fullInfo: ${fullInfo?.url || ''}, reducedInfo: ${reducedInfo?.url || ''}, venueId: ${venueId} >>`);
        } catch (err) {
          console.error(`11. DB Create completed-> log ${err}`);
        }

        const dbLocal = PouchLoadService.getDbLocal();
        PouchSyncService.createIndexes(dbLocal);
        dbLocal.viewCleanup();
        service.setup = true;
        try {
          console.log(logTag, `11.2 CreateDatabase finished localPouchName: ${localPouchName}, fullInfo: ${fullInfo?.url || ''}, reducedInfo: ${reducedInfo?.url || ''}, venueId: ${venueId} >>`);
        } catch (err) {
          console.error(`11.2 CreateDatabase finished-> log ${err}`);
        }
        // Calculate creation time
        const endTime = new Date().getTime();
        const notes = `Took ${endTime - startTime} milliseconds`;
        AdminService.serverLoggingDbEvents(venueId, "CreateDatabase progress-info", AdminServiceLog.INFOLOG, notes);
        try {
          syncLogStack.push(`11.2 CreateDatabase finished localPouchName: ${localPouchName}, fullInfo: ${fullInfo?.url || ''}, reducedInfo: ${reducedInfo?.url || ''}, venueId: ${venueId} >>`);
        } catch (err) {
          console.error(`11.2 CreateDatabase finished-> log ${err}`);
        }

        // Sends createDatabase execution stack trace to SEQ logging
        AdminService.serverLoggingDbEvents(venueId, "CreateDatabase stack-trace", AdminServiceLog.INFOLOG, syncLogStack.toString())

        return true;
      }));
  }

  function throwErrorOnMismatch(venueId, messageId, _currentVenueIdLoading) {
    if (currentVenueIdLoading !== _currentVenueIdLoading) {
      console.error('VenueID mismatch', venueId, currentVenueIdLoading, _currentVenueIdLoading);
      AdminService.serverLoggingDbEvents(venueId,
        `VenueID mismatch ${messageId}: Latest venue id (${currentVenueIdLoading}) does not match the one currently loading (${_currentVenueIdLoading}).`,
        AdminServiceLog.ERRORLOG, venueId.toString());
      throw "VenueID mismatch";
    }
  }



  /********************************************************************************************************************************************************** */
  /***************************************************************** REPLICATE AND SYNC ********************************************************************* */
  /********************************************************************************************************************************************************** */


  /** Replicate all the missing changes of the remote DB into the local one. Resolve on job completed */
  function initialReplicateFrom(isOnlineCreated, retryCount, inheritedResolve, inheritedReject, inheritedError) {

    syncSuccess = false;

    return PouchSyncService.initialReplicateFrom({
      serverLoggingDbEvents: (message, logType, logNotes) => {
        AdminService.serverLoggingDbEvents(venueId, message, logType, logNotes);
      },
      initialReplicaChangeCallback: (value) => {
        $rootScope.$broadcast('pouch-initial-replica-change', value);
      },
      upToDateWithRemoteCallback: () => {
        $rootScope.$broadcast('uptodate-with-remote');
        syncSuccess = true;
      },
      unauthorisedCallback: () => $rootScope.$broadcast('unauthorised'),
      dbLocal: PouchLoadService.getDbLocal(),
      reducedInfo: service.settings.reducedInfo,
      syncLogStack,
      // removed 'replicateSubscriptions' and now set using PouchSyncService.setReplicateSubscriptions
      remoteDBDocsCount,
      isOnlineCreated,
      retryCount,
      inheritedResolve,
      inheritedReject,
      inheritedError
    });
  }


  function liveSync() {
    PouchSyncService.liveSync({
      dbLocal: PouchLoadService.getDbLocal(),
      syncLogStack,
      replicateSubscriptions,
      reducedInfo: service.settings.reducedInfo,
      handleReplicationFrom,
      handleReplicationTo
    });
  }

  function handleReplicationFrom(eventType, info) {
    switch (eventType) {

      case ReplicationEventType.uptodate:
        console.log(logTag, '99. Sync UpToDate!! [uptodate]  >>');
        if (!window.config.pouch.initialReplicate) {
          $rootScope.$broadcast('uptodate-with-remote');
        }
        NetworkConnectionService.setCouchStatus(CouchStatus.uptodate);
        break;
      case ReplicationEventType.error:
        console.log(logTag, '99. Sync error!! [ERROR]  >>');
        if (info && info.status === 401) {
          $rootScope.$broadcast('unauthorised');
        }
        console.error(info);
        NetworkConnectionService.delayCouchStatus(4000, CouchStatus.offline);
        break;
      case ReplicationEventType.change:
        console.log(logTag, '99. Sync event!! [change]  >>');
        if (Environment.IsLocal) {
          for (const doc of info.docs) {
            console.info('Change <=', doc._id, doc);
          }
        }
        // $rootScope.$broadcast('receive-change', info.docs);
        PouchActions.updatePouchSyncedDoctype(PouchDocType.receiveChange, info.docs);

        if (info.docs_written > service.session.docsReceived) {
          service.session.docsReceived = info.docs_written;
          NetworkConnectionService.setCouchStatus(CouchStatus.receiving);
        }
        break;
    }
  }

  function handleReplicationTo(eventType, info) {
    switch (eventType) {
      case ReplicationEventType.uptodate:
        NetworkConnectionService.resetCouchStatusLocalChanges();
        NetworkConnectionService.setCouchStatus(CouchStatus.uptodate);
        break;
      // case ReplicationEventType.offline:
      //   setSessionStatus(service.OFFLINE);
      //   break;
      case ReplicationEventType.error:
        if (info && info.status === 401) {
          $rootScope.$broadcast('unauthorised');
        }
        console.error(info);
        NetworkConnectionService.delayCouchStatus(2000, CouchStatus.offline);
        break;
      case ReplicationEventType.change:
        $rootScope.$broadcast('send-change', info.docs);
        if (info.docs_written > service.session.docsSent) {
          service.session.docsSent = info.docs_written;
          NetworkConnectionService.setCouchStatus(CouchStatus.sending);
        }
        break;
    }
  }

  /********************************************************************************************************************************************************** */
  /************************************************************** CHANGES && LISTENERS ********************************************************************** */
  /********************************************************************************************************************************************************** */

  function addListeners() {
    console.log(logTag, '6. Adding Listeners  >>');
    syncLogStack.push('6. Adding Listeners  >>');

    // listen for changes
    addChangeListener(PouchLoadService.getDbLocal());

    NetworkConnectionService.addNavigatorListeners();

    // Set the name of the hidden property and the change event for visibility
    var hidden, visibilityChange;
    if (typeof document.hidden !== "undefined") { // Opera 12.10 and Firefox 18 and later support
      hidden = "hidden";
      visibilityChange = "visibilitychange";
    } else if (typeof document.msHidden !== "undefined") {
      hidden = "msHidden";
      visibilityChange = "msvisibilitychange";
    } else if (typeof document.webkitHidden !== "undefined") {
      hidden = "webkitHidden";
      visibilityChange = "webkitvisibilitychange";
    }

    document.addEventListener(visibilityChange, function () {
      if (document[hidden]) {
        isActive = false;
      } else {
        isActive = true;
      }
      syncComplete();
    }, false);

    return $q(function (resolve) {
      resolve();
    });
  }


  function addChangeListener(db, local) {
    // listen for changes
    return db.changes(
      {
        since: 'now',
        live: true,
        timeout: false
      })
      .on('change', handleChanges)
      .on('error', function (err) {
        if (err && err.status === 401) {
          $rootScope.$broadcast('unauthorised');
        }
        console.error(logTag, 'addChangeListener', err);
      })
      .on('complete', function () {
        if (local) {
          // changes cancelled
          setTimeout(function () {
            addChangeListener(db, local);
          }, 500);
        }
      });
  }

  function addOnlineListener() {
    removeOnlineListener();
    onlineChanges = addChangeListener(PouchLoadService.getDbOnline());
  }

  function removeOnlineListener() {
    if (onlineChanges) {
      try {
        onlineChanges.cancel();
      } catch (err) {
        console.error(logTag, 'removeOnlineListener', err);
      }
    }
  }


  function handleChanges(evt) {
    if (!syncing) {
      syncing = true;
      // this is a new series of syncing
      // clear the syncTypes
      syncTypes = {};
    }

    NetworkConnectionService.incrementCouchStatusLocalChanges();
    // keep track of the doc types
    var docType = evt.id.split('_')[0];

    if (!updatedDocIds[docType]) {
      updatedDocIds[docType] = [];
    }
    updatedDocIds[docType].push(evt.id);

    $rootScope.$broadcast('db-change', evt.id);
    syncTypes[docType] = docType;
    if (isActive) {
      changesInBatch++;
      // clear the sync interval
      $window.clearTimeout(syncCheckInterval);
      if (changesInBatch > maxChangeBatch) {
        // call syncComplete right away as we have a big enough batch
        syncComplete();
      } else {
        // wait to fire syncComplete in case their are other changes
        syncCheckInterval = $window.setTimeout(syncComplete, 200);
      }
    }
    // Report logging
    if (docType === 'booking') {
      TelemetryService.track(
        'bookingPoint', [
        ['booking', 0],
        ['rev', evt.changes[0].rev]
      ], [
        ['id', evt.id],
        ['status', 'update']
      ]
      );
    }
  }

  function syncComplete() {
    changesInBatch = 0;
    // clear the sync interval
    $window.clearTimeout(syncCheckInterval);
    for (var docType in syncTypes) {

      const actionName = PouchActions.getTagActionName(docType);
      if (actionName) {
        // $rootScope.$broadcast('pouch-synced-doctype-' + docType, updatedDocIds[docType]);
        PouchActions.updatePouchSyncedDoctype(docType, updatedDocIds[docType]);
      } else {
        // @todo: add back in console.warn when all TagActionNames have been completed
        // docType && console.warn(NS, "you need to specify a valid PouchDocType", docType);
        docType && PouchActions.updatePouchSyncedDoctypeBeta(docType, updatedDocIds[docType]);
      }
    }
    // clear the syncTypes
    syncTypes = {};
    updatedDocIds = {};
    // syning is false
    syncing = false;
  }

  /********************************************************************************************************************************************************** */
  /****************************************************************** CLOSE AND CHECK *********************************************************************** */
  /********************************************************************************************************************************************************** */

  /** Request close of replication and changes, need to resolve checkDatabaseClosed to guarantee DB is closed */
  function closeDatabase() {
    PouchLoadService.closeDatabase(syncLogStack, replicateSubscriptions, syncCheckInterval);

    // Reset to initial status
    service.setup = false;
    service.session = {
      docsSent: 0,
      docsReceived: 0,
      localChanges: [],
      remoteChanges: []
    };
  }

  function destroyDatabases(reload, venueId) {
    closeDatabase();
    $localStorage.couchUsers = null;

    return PouchDB.allDbs().then(function (dbs) {
      let destroyAllDBsPromise = Promise.resolve();
      for (const db of dbs) {
        if (!venueId || db.startsWith('db' + venueId)) { // if venue id is provided, destroy only relevant databases TODO remind Dave to document this naming convention
          destroyAllDBsPromise = destroyAllDBsPromise.then(() => {
            console.log('Destroying', db);
            return new PouchDB(db).destroy();
          });
        }
      }

      destroyAllDBsPromise.then(() => {
        if (reload) {
          window.location.reload();
        }
      }).catch(function (err) {
        console.error(err);
      });
    });
  }

  /********************************************************************************************************************************************************** */
  /***************************************************************function loadDump move to pouch-bulk-service*********************************************** */


  /********************************************************************************************************************************************************** */
  /*********************************************************************** OTHERS *************************************************************************** */
  /********************************************************************************************************************************************************** */

  function getRemoteDatebaseInfo(remoteURL, username, password) {
    var authdata = btoa(username + ':' + password);
    return $http.get(remoteURL, {
      headers: {
        'Authorization': 'Basic ' + authdata
      }
    });
  }

  function getNewId(preParts, simple, postParts) {
    var parts = preParts.concat([UtilsService.generateRandomCode()]);

    if (!simple) {
      parts.push(new Date().getTime());
    }

    if (postParts) {
      parts = parts.concat(postParts);
    }

    return parts.join('_');
  }

  /**
   * Moved selectOnlineDatabaseAsFirstOption to PouchLoadService
   */

  function getLocalDbUpperDateLimit() {
    const timezoneId = AdminService.getVenueTimezoneId();
    var now = DateUtilsService.getVenueTime(timezoneId).setHours(0, 0, 0, 0);
    var backTime = daysInHistory * (1000 * 60 * 60 * 24);
    return new Date(now - backTime);
  }

  function changeDate(evt, date) {

    // console.log('PouchService - change-date', date);

    if (PouchLoadService.isDateInLocalDBRange(date)) {
      removeOnlineListener();
    } else {
      addOnlineListener();
    }
  }

  function getNewOfflineBookingsInstance() {
    return new PouchDB('offlineBookings');
  }


  return service;
}
